import { createLucideIcon } from 'lucide-react';

/**
 * Ícones médicos desenhados para o Ryan — temas sem ícone na Lucide (rim, vias
 * urinárias, glomérulo, serpente, gestante, útero). Feitos com createLucideIcon
 * no mesmo grid 24×24 / traço 2 da Lucide, então aceitam as mesmas props
 * (size, color, strokeWidth...) e convivem com os demais em RYAN_MEDICAL_ICON_MAP.
 *
 * Ids com prefixo "Custom" para nunca colidir com um ícone Lucide de mesmo nome.
 * Ids ficam gravados em subdeck.iconId — não renomear depois de publicado.
 */

const KIDNEY_OUTLINE =
  'M14.5 2.6C10 1.6 5 4.6 5 10.6s3.6 11.3 9 10.9c2.5-.2 3.5-2.5 2.5-4.3-.8-1.5-2.5-2.2-2.5-4.2v-1.5c0-2 1.7-2.7 2.5-4.2 1-1.8.6-4.2-2-4.7z';
const KIDNEY_URETER = 'M14 12h2.5c1.4 0 2.5 1.1 2.5 2.5V22';

// Rim (com ureter saindo do hilo)
export const CustomKidney = createLucideIcon('CustomKidney', [
  ['path', { d: KIDNEY_OUTLINE, key: 'outline' }],
  ['path', { d: KIDNEY_URETER, key: 'ureter' }],
]);

// Nefrolitíase — rim com cálculo
export const CustomKidneyStone = createLucideIcon('CustomKidneyStone', [
  ['path', { d: KIDNEY_OUTLINE, key: 'outline' }],
  ['path', { d: 'm8.5 10.5 1.8-1.3 1.7 1-.3 2-1.8.8-1.5-1z', key: 'stone' }],
  ['path', { d: KIDNEY_URETER, key: 'ureter' }],
]);

// Rins e vias urinárias — rins, ureteres, bexiga e uretra
export const CustomUrinaryTract = createLucideIcon('CustomUrinaryTract', [
  ['path', { d: 'M5 2.5c-1.8 0-3 1.7-3 4.2S3.2 11 5 11c1.3 0 2.1-.9 1.8-2-.2-.7-.8-1.1-.8-2.3s.6-1.6.8-2.3c.3-1.1-.5-1.9-1.8-1.9z', key: 'kidney-l' }],
  ['path', { d: 'M19 2.5c1.8 0 3 1.7 3 4.2S20.8 11 19 11c-1.3 0-2.1-.9-1.8-2 .2-.7.8-1.1.8-2.3s-.6-1.6-.8-2.3c-.3-1.1.5-1.9 1.8-1.9z', key: 'kidney-r' }],
  ['path', { d: 'M6.8 6.7c1.6 0 2.2 1.6 2.7 4.3.3 1.7.6 3 1 3.6', key: 'ureter-l' }],
  ['path', { d: 'M17.2 6.7c-1.6 0-2.2 1.6-2.7 4.3-.3 1.7-.6 3-1 3.6', key: 'ureter-r' }],
  ['path', { d: 'M8.5 15.5c0-.7.6-1 1.4-1h4.2c.8 0 1.4.3 1.4 1 0 2.6-1.6 4.5-3.5 4.5s-3.5-1.9-3.5-4.5z', key: 'bladder' }],
  ['path', { d: 'M12 20v2', key: 'urethra' }],
]);

// Bexiga (com nível de urina) — ITU/cistite
export const CustomBladder = createLucideIcon('CustomBladder', [
  ['path', { d: 'M12 6c3.4 0 6 2.5 6 5.8 0 3.5-2.7 6.2-6 6.2s-6-2.7-6-6.2C6 8.5 8.6 6 12 6z', key: 'bladder' }],
  ['path', { d: 'M8.6 13c1.1.8 2.3.8 3.4 0s2.3-.8 3.4 0', key: 'urine' }],
  ['path', { d: 'M8.5 7 6 2', key: 'ureter-l' }],
  ['path', { d: 'M15.5 7 18 2', key: 'ureter-r' }],
  ['path', { d: 'M12 18v4', key: 'urethra' }],
]);

// Glomérulo — tufo capilar na cápsula de Bowman (glomerulopatias)
export const CustomGlomerulus = createLucideIcon('CustomGlomerulus', [
  ['path', { d: 'M7.5 19.6A8 8 0 1 1 16.5 19.6', key: 'capsule' }],
  ['path', { d: 'M16.5 19.6c.3 1.4 1.4 2.4 3.5 2.4', key: 'tubule' }],
  ['path', { d: 'M10.4 2v6', key: 'afferent' }],
  ['path', { d: 'M13.6 2v6', key: 'efferent' }],
  ['circle', { cx: '10', cy: '10.6', r: '2.2', key: 'loop-1' }],
  ['circle', { cx: '14', cy: '10.6', r: '2.2', key: 'loop-2' }],
  ['circle', { cx: '12', cy: '14.2', r: '2.2', key: 'loop-3' }],
]);

// Serpente — acidente ofídico
export const CustomSnake = createLucideIcon('CustomSnake', [
  ['path', { d: 'M2 20.5h10a3.25 3.25 0 0 0 0-6.5H9.5a3.25 3.25 0 0 1 0-6.5H14', key: 'body' }],
  ['ellipse', { cx: '17.6', cy: '7.5', rx: '3.6', ry: '2.3', key: 'head' }],
  ['path', { d: 'M21.2 7.5h1.2l.8-.9M22.4 7.5l.8.9', key: 'tongue' }],
]);

// Gestante (perfil)
export const CustomPregnantWoman = createLucideIcon('CustomPregnantWoman', [
  ['circle', { cx: '10', cy: '3.5', r: '2', key: 'head' }],
  ['path', { d: 'M9 22v-6.5L8 13V9.5C8 8.1 9.1 7 10.5 7c1.2 0 2.1.8 2.4 1.9.2.8.9 1.2 1.6 1.6 1.5.9 2.5 2.4 2.5 4.1 0 2.1-1.7 3.4-4 3.4', key: 'body' }],
  ['path', { d: 'M13 18v4', key: 'leg' }],
]);

// Útero, trompas e ovários
export const CustomUterus = createLucideIcon('CustomUterus', [
  ['path', { d: 'M9 8.5h6v4.8c0 1.9-.9 3.4-2 4V21h-2v-3.7c-1.1-.6-2-2.1-2-4z', key: 'uterus' }],
  ['path', { d: 'M9 9.5C7.4 9.5 6.4 8 4.5 8', key: 'tube-l' }],
  ['path', { d: 'M15 9.5c1.6 0 2.6-1.5 4.5-1.5', key: 'tube-r' }],
  ['circle', { cx: '4', cy: '11', r: '1.6', key: 'ovary-l' }],
  ['circle', { cx: '20', cy: '11', r: '1.6', key: 'ovary-r' }],
]);

// Útero gravídico (feto) — fisiologia da gestação, parto, abortamento
export const CustomGravidUterus = createLucideIcon('CustomGravidUterus', [
  ['path', { d: 'M12 2.5c4.4 0 7.5 3.5 7.5 8 0 4.4-2.6 7.6-5.5 8.6V22h-4v-2.9C7.1 18.1 4.5 14.9 4.5 10.5c0-4.5 3.1-8 7.5-8z', key: 'uterus' }],
  ['circle', { cx: '13.2', cy: '8', r: '2', key: 'fetus-head' }],
  ['path', { d: 'M11.3 9c-1.9.9-2.6 3.3-1.3 5 1.2 1.6 3.6 1.6 4.8.3', key: 'fetus-body' }],
]);
